import React from 'react';
import styled from 'styled-components';
import * as R from 'ramda';
import { animated } from 'react-spring';
import Box from 'ui/Box';
import { Filter } from 'rangeConfig/@types';
import { useIntl } from 'react-intl';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { Product } from '@inter-ikea-storageone/range';
import { useSpring } from 'react-spring';
import useMeasure from 'hooks/useMeasure';
import { FilterState, SelectedProductFilters } from 'selectedMenu/types';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { filterHasCorrectOptionsLength } from 'filters/SizeFilter/utils';
import Title, { HeadingType } from 'ui/Typography/Title';
import Divider from 'ui/Divider/Divider';

const AnimationWrapper = styled(animated.div)`
  overflow: hidden;
`;

type Props = {
  availableFilters: Filter[];
  products: Product[];
  filterProducts: (id: string, productIdList: string[], filterState: FilterState) => void;
  isFilterVisible: boolean;
  selectedProductFilters: SelectedProductFilters;
  noAnimationWrapper?: boolean;
};

function SelectedMenuFilters({
  availableFilters,
  products,
  filterProducts,
  isFilterVisible,
  selectedProductFilters,
  noAnimationWrapper
}: Props) {
  const { formatMessage } = useIntl();

  const [ref, { height }] = useMeasure();

  const springProps = useSpring({
    to: {
      height: isFilterVisible ? height + 32 : 0,
      opacity: isFilterVisible ? 1 : 0
    },
    from: {
      height: 0,
      opacity: 0
    }
  });

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const selectedGroup = R.find(R.propEq('id', 'frames'), productGroups);

  const filtersAndToggles: Filter[] = [...availableFilters];

  const checkedFiltersAndToggles = filtersAndToggles.filter(filter =>
    filterHasCorrectOptionsLength(filter.id, products)
  );

  const filter = (id: string, productIdList: string[], filterState: FilterState) => {
    filterProducts(id, productIdList, filterState);
  };

  return (
    <>
      {checkedFiltersAndToggles && (
        <>
          {noAnimationWrapper ? (
            <Box {...ref}>
              {selectedGroup &&
                checkedFiltersAndToggles.map(
                  ({ component: Component, title, type, id, ...props }) => (
                    <div key={id}>
                      <Title
                        mb="md"
                        heading={HeadingType.HeadingThree}
                        type={ThemeFontStyleTypeEnum.headingSmall}
                      >
                        {title && formatMessage({ id: title })}
                      </Title>
                      <Component
                        onFilter={(productIdList: string[], filterState: FilterState) =>
                          filter(id, productIdList, filterState)
                        }
                        products={products}
                        filterState={selectedProductFilters[id]?.filterState || {}}
                        type={type}
                        isFilterVisible={isFilterVisible}
                        {...props}
                      />
                    </div>
                  )
                )}
            </Box>
          ) : (
            <AnimationWrapper style={springProps}>
              <Box mx="s" mt="xl" {...ref}>
                {selectedGroup &&
                  checkedFiltersAndToggles.map(
                    ({ component: Component, title, type, id, ...props }) => (
                      <div key={id}>
                        <Title
                          mb="md"
                          heading={HeadingType.HeadingThree}
                          type={ThemeFontStyleTypeEnum.headingXSmall}
                        >
                          {title && formatMessage({ id: title })}
                        </Title>
                        <Component
                          onFilter={(productIdList: string[], filterState: FilterState) =>
                            filter(id, productIdList, filterState)
                          }
                          products={products}
                          filterState={selectedProductFilters[id]?.filterState || {}}
                          type={type}
                          isFilterVisible={isFilterVisible}
                          {...props}
                        />
                      </div>
                    )
                  )}
                <Divider />
              </Box>
            </AnimationWrapper>
          )}
        </>
      )}
    </>
  );
}

export default SelectedMenuFilters;
